import {
  CSSProperties,
  HTMLAttributes,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Image } from './Image';
import { PlayIcon } from './PlayIcon';
import styles from './RoundStory.module.css';
import { Video } from './Video';
import {
  STORY_LINE_CLAMP,
  STORY_ROUND_ASPECT_RATIO,
  STORY_ROUND_PULSE_SIZE,
} from '../constants';
import { ImageFormat } from '../enums';
import { FileFragment } from '../graphql/generated/graphql';
import { Story } from '../types';
import { getVisibleLines } from '../utils';
import { StoryStyle } from './Story';

interface Props extends HTMLAttributes<HTMLDivElement> {
  preload?: HTMLVideoElement['preload'];
  story: Story;
  video?: FileFragment;
  videoRef?: RefObject<HTMLVideoElement>;
  borderColor: CSSProperties['borderColor'];
  font: CSSProperties['font'];
  isPulsating: boolean;
  withBorder?: boolean;
  withOverlay: boolean;
  withPlayIcon: boolean;
  withTitle: boolean;
  style?: StoryStyle;
}

export const RoundStory = ({
  preload,
  story,
  video,
  videoRef,
  borderColor,
  font,
  isPulsating,
  withBorder,
  withOverlay,
  withPlayIcon,
  withTitle,
  style,
  ...divProps
}: Props) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [lineClamp, setLineClamp] = useState(STORY_LINE_CLAMP);

  useEffect(() => {
    if (textRef.current) {
      const textVisibleLines = getVisibleLines(textRef.current);
      setLineClamp(
        textVisibleLines <= STORY_LINE_CLAMP
          ? textVisibleLines
          : STORY_LINE_CLAMP
      );
    }
  }, [story.Title, font]);

  return (
    <div
      className={styles.round_wrapper}
      style={
        {
          '--pulse-size': withBorder ? `${STORY_ROUND_PULSE_SIZE}vh` : `1vh`,
          aspectRatio: withTitle
            ? STORY_ROUND_ASPECT_RATIO.WITH_TITLE
            : STORY_ROUND_ASPECT_RATIO.WITHOUT_TITLE,
          ...style,
        } as CSSProperties
      }
      {...divProps}
    >
      <div
        className={`${styles.round_border_wrapper} ${withBorder && styles.round_border} ${withBorder && isPulsating ? styles.pulsating : ''}`}
        style={
          {
            '--border-color': borderColor,
            borderWidth: style?.borderWidth,
          } as CSSProperties
        }
      >
        <div className={styles.round_media_wrapper}>
          {!video && (
            <Image
              className={styles.round_image}
              file={story.PreviewImage}
              alt={story.PreviewImage.alternativeText || ''}
              fallbackFormat={ImageFormat.Thumbnail}
              sizes='100vh'
            />
          )}
          {withOverlay && <div className={styles.round_overlay} />}
          {withPlayIcon && <PlayIcon className={styles.play_icon} />}
          {video && (
            <Video
              className={styles.round_video}
              ref={videoRef}
              file={video}
              preload={preload}
            />
          )}
        </div>
      </div>
      {withTitle && (
        <p
          ref={textRef}
          className={styles.round_text}
          style={{
            font,
            lineClamp,
            WebkitLineClamp: lineClamp,
          }}
        >
          {story.Title}
        </p>
      )}
    </div>
  );
};
